<template>
  <div class="entry-profile">
    <p v-if="loadState === 'error'" class="load-error" role="alert">{{ $t("entryProfile.loadError") }}</p>

    <template v-else-if="loadState === 'loaded'">
      <button type="button" class="edit-user-button" @click="onClickEditUserInfo">
        {{ $t("entryProfile.editUserInfo") }}
      </button>

      <my-page-user-info-edit-dialog v-if="showEditDialog" :profile="profile" :nola-penname="nolaPenname"
        @close="showEditDialog = false" @updated="onUserInfoUpdated" />

      <hr class="divider" />

      <p class="history-description">{{ $t("entryProfile.historyDescription") }}</p>

      <div class="field">
        <div class="field-header">
          <label class="field-label" for="history-of-awards">{{ $t("entryProfile.historyOfAwards.label") }}</label>
          <span class="field-count">{{ historyOfAwards.length }}/{{ historyMaxLength }}</span>
        </div>
        <textarea id="history-of-awards" v-model="historyOfAwards" class="field-textarea" :class="{ error: !!error }"
          :maxlength="historyMaxLength" :placeholder="$t('entryProfile.historyOfAwards.placeholder')" />
        <p v-if="error" class="field-error">{{ error }}</p>
      </div>

      <div class="footer">
        <button type="button" class="update-button" :disabled="saving" @click="onClickUpdate">
          {{ $t("entryProfile.updateHistory") }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { z } from "zod";
import { NolaPfContestClient } from "@/lib/clients";
import { User } from "@/lib/models";
import { NolaPfContestProfile } from "@/lib/models/nolaPfContest";
import MyPageUserInfoEditDialog from "@/components/ui/MyPageUserInfoEditDialog.vue";

const nolaPfContestClient = new NolaPfContestClient();

const HISTORY_MAX_LENGTH = 500;

type ProfileLoadState = "loading" | "loaded" | "error";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    MyPageUserInfoEditDialog,
  },
  data() {
    return {
      profile: null as NolaPfContestProfile | null,
      historyOfAwards: "",
      loadState: "loading" as ProfileLoadState,
      showEditDialog: false,
      saving: false,
      error: "",
    };
  },
  computed: {
    historyMaxLength() {
      return HISTORY_MAX_LENGTH;
    },
    nolaPenname() {
      const user = this.$store.getters["userModule/user"] as User | undefined;
      return user?.name ?? "";
    },
  },
  async created() {
    const profilePromise = nolaPfContestClient.fetchProfile();
    const userPromise = this.$store.dispatch("userModule/initialize");
    try {
      const [profile] = await Promise.all([profilePromise, userPromise]);
      const user = this.$store.getters["userModule/user"] as User | undefined;
      if (!user?.userId) {
        throw new Error("Failed to fetch user");
      }
      this.profile = profile;
      this.historyOfAwards = this.profile?.historyOfAwards ?? "";
      this.loadState = "loaded";
    } catch (error) {
      console.error("Failed to fetch contest profile:", error);
      this.loadState = "error";
    }
  },
  methods: {
    onClickEditUserInfo() {
      this.showEditDialog = true;
    },
    onUserInfoUpdated(profile: NolaPfContestProfile) {
      this.profile = profile;
    },
    validate(): boolean {
      const schema = z.object({
        historyOfAwards: z.string().max(HISTORY_MAX_LENGTH),
      });
      const result = schema.safeParse({ historyOfAwards: this.historyOfAwards });
      this.error = result.success ? "" : (this.$t("entryProfile.validation.historyOfAwards") as string);
      return result.success;
    },
    async onClickUpdate() {
      if (this.saving) return;
      if (!this.validate()) return;

      this.saving = true;
      try {
        const result = await nolaPfContestClient.updateProfile({
          historyOfAwards: this.historyOfAwards,
        });
        this.profile = result;
        this.historyOfAwards = result.historyOfAwards;
        this.$notify({
          title: this.$t("common.saved") as string,
          type: "success",
        });
      } catch (error) {
        console.error("Failed to update contest profile:", error);
        this.$notify({
          title: this.$t("entryProfile.updateError") as string,
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
});

interface Props { }

interface Data {
  profile: NolaPfContestProfile | null;
  historyOfAwards: string;
  loadState: ProfileLoadState;
  showEditDialog: boolean;
  saving: boolean;
  error: string;
}

interface Computed {
  historyMaxLength: number;
  nolaPenname: string;
}

interface Methods {
  onClickEditUserInfo(): void;
  onUserInfoUpdated(profile: NolaPfContestProfile): void;
  validate(): boolean;
  onClickUpdate(): Promise<void>;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.entry-profile {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 40px 48px;
  overflow: auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.history-description {
  margin: 24px 0 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.edit-user-button {
  position: relative;
  align-self: flex-start;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.divider {
  width: 100%;
  height: 1px;
  margin: 24px 0 0;
  border: none;
  background-color: var(--color-border);
}

.field {
  margin-top: 16px;
}

.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.field-label,
.field-count {
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.field-textarea {
  display: block;
  width: 100%;
  min-height: 180px;
  padding: 12px 16px;
  border: none;
  border-radius: var(--radius-md);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: var(--line-height-default);
  resize: none;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &.error {
    box-shadow: 0 0 0 1px var(--color-warning);
  }
}

.field-error {
  margin: 4px 4px 0;
  font-size: var(--font-size-xs);
  color: var(--color-warning);
}

.footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.update-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.load-error {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-warning);
}

@include mq("sp") {
  .entry-profile {
    height: 100%;
    min-height: 0;
    padding: 24px 16px;
  }
}
</style>
